/** Bitrix-inspired task / task-template form styling (light + dark) */

import { alpha } from "@mui/material/styles";

/** Align with app dark theme (`background.default` / `background.paper`). */
export const TASK_FORM_DARK_BG = "#121212";
export const TASK_FORM_DARK_SURFACE = "#1E1E1E";
export const TASK_FORM_DARK_INPUT = "#1E1E1E";
export const TASK_FORM_DARK_NESTED = "#252525";
export const TASK_FORM_DARK_TEXT = "#f1f5f9";
export const TASK_FORM_DARK_MUTED = "rgba(226, 232, 240, 0.78)";
export const TASK_FORM_DARK_BORDER = "rgba(148, 163, 184, 0.45)";

export const TASK_FORM_LIGHT_PAGE_BG = "#eef2f4";
export const TASK_FORM_LIGHT_SECTION_BG = "#ffffff";
export const TASK_FORM_LIGHT_SOFT_BG = "#f5f7f8";
export const TASK_FORM_LIGHT_BORDER = "#dfe0e3";
export const TASK_FORM_LIGHT_LABEL = "#525c69";
export const TASK_FORM_LIGHT_TEXT = "#333333";

const isDark = (theme) => theme.palette.mode === "dark";

export const taskFormDarkContainerSx = {
  bgcolor: (theme) => (isDark(theme) ? TASK_FORM_DARK_BG : TASK_FORM_LIGHT_PAGE_BG),
  color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : TASK_FORM_LIGHT_TEXT),
  fontFamily: (theme) =>
    isDark(theme)
      ? undefined
      : '"Helvetica Neue", Helvetica, Arial, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  "& .MuiDialogTitle-root, & .MuiDialogContent-root, & .MuiDialogActions-root": {
    bgcolor: (theme) => (isDark(theme) ? TASK_FORM_DARK_BG : "transparent"),
    color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : TASK_FORM_LIGHT_TEXT),
  },
  "& .MuiTypography-root": {
    color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : TASK_FORM_LIGHT_TEXT),
  },
  "& .MuiTypography-colorTextSecondary": {
    color: (theme) => (isDark(theme) ? TASK_FORM_DARK_MUTED : TASK_FORM_LIGHT_LABEL),
  },
  "& .MuiDivider-root": {
    borderColor: (theme) => (isDark(theme) ? TASK_FORM_DARK_BORDER : TASK_FORM_LIGHT_BORDER),
  },
  "& .MuiInputBase-root": {
    color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : TASK_FORM_LIGHT_TEXT),
    bgcolor: (theme) => (isDark(theme) ? TASK_FORM_DARK_INPUT : TASK_FORM_LIGHT_SECTION_BG),
    fontSize: (theme) => (isDark(theme) ? "0.975rem" : "1rem"),
  },
  "& .MuiInputBase-input::placeholder": {
    color: (theme) => (isDark(theme) ? TASK_FORM_DARK_MUTED : TASK_FORM_LIGHT_LABEL),
    opacity: 1,
  },
  "& .MuiInput-underline:before": {
    borderBottomColor: (theme) => (isDark(theme) ? TASK_FORM_DARK_BORDER : TASK_FORM_LIGHT_BORDER),
  },
  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
    borderBottomColor: (theme) => (isDark(theme) ? TASK_FORM_DARK_MUTED : "#a8adb4"),
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: (theme) => theme.palette.primary.main,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: (theme) => (isDark(theme) ? TASK_FORM_DARK_BORDER : TASK_FORM_LIGHT_BORDER),
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: (theme) => (isDark(theme) ? TASK_FORM_DARK_MUTED : "#a8adb4"),
  },
  "& .MuiFormControlLabel-label, & .MuiIconButton-root": {
    color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : TASK_FORM_LIGHT_TEXT),
  },
  "& .Mui-disabled": {
    WebkitTextFillColor: (theme) =>
      isDark(theme) ? `${TASK_FORM_DARK_MUTED} !important` : undefined,
  },
};

/** Outer edge for task / template modals in dark mode (single box around form + chat). */
export const taskFormDarkOuterBorderSx = {
  border: (theme) =>
    isDark(theme) ? `1px solid rgba(255, 255, 255, 0.14)` : "none",
  boxSizing: "border-box",
};

export const taskFormPaperSx = (options = {}) => ({
  ...taskFormDarkContainerSx,
  ...taskFormDarkOuterBorderSx,
  borderRadius: (theme) => (isDark(theme) ? undefined : "16px"),
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  minHeight: options.minHeight ?? { xs: "72vh", sm: 580 },
  maxHeight: options.maxHeight ?? "92vh",
  ...options.extra,
});

export const taskFormSectionSx = {
  border: (theme) =>
    `1px solid ${isDark(theme) ? TASK_FORM_DARK_BORDER : TASK_FORM_LIGHT_BORDER}`,
  borderRadius: (theme) => (isDark(theme) ? 2 : "12px"),
  bgcolor: (theme) => (isDark(theme) ? TASK_FORM_DARK_SURFACE : TASK_FORM_LIGHT_SECTION_BG),
  color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : TASK_FORM_LIGHT_TEXT),
  boxShadow: (theme) =>
    isDark(theme) ? "none" : "0 1px 3px rgba(15, 23, 42, 0.06)",
};

export const taskFormSectionPadding = { xs: 1.75, sm: 2.25 };

export const taskFormNestedBoxSx = {
  border: (theme) =>
    `1px solid ${isDark(theme) ? TASK_FORM_DARK_BORDER : TASK_FORM_LIGHT_BORDER}`,
  borderRadius: (theme) => (isDark(theme) ? 1.5 : "10px"),
  bgcolor: (theme) =>
    isDark(theme) ? TASK_FORM_DARK_NESTED : TASK_FORM_LIGHT_SOFT_BG,
  color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : TASK_FORM_LIGHT_TEXT),
};

export const taskFormSoftBoxSx = {
  bgcolor: (theme) =>
    isDark(theme) ? TASK_FORM_DARK_SURFACE : TASK_FORM_LIGHT_SOFT_BG,
  border: (theme) =>
    `1px solid ${isDark(theme) ? TASK_FORM_DARK_BORDER : TASK_FORM_LIGHT_BORDER}`,
  borderRadius: (theme) => (isDark(theme) ? 1 : "8px"),
  color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : TASK_FORM_LIGHT_TEXT),
};

export const taskFormFloatingButtonSx = {
  bgcolor: (theme) =>
    isDark(theme) ? TASK_FORM_DARK_SURFACE : TASK_FORM_LIGHT_SECTION_BG,
  border: (theme) =>
    `1px solid ${isDark(theme) ? TASK_FORM_DARK_BORDER : TASK_FORM_LIGHT_BORDER}`,
};

export const taskFormFieldLabelSx = {
  color: (theme) => (isDark(theme) ? TASK_FORM_DARK_MUTED : TASK_FORM_LIGHT_LABEL),
  fontSize: { xs: "0.875rem", sm: "0.9375rem" },
  fontWeight: 500,
  lineHeight: 1.4,
  whiteSpace: "nowrap",
};

export const taskFormFieldLabelRowSx = {
  ...taskFormFieldLabelSx,
  alignSelf: "center",
};

export const taskFormSectionTitleSx = {
  fontWeight: 600,
  fontSize: { xs: "0.9rem", sm: "0.975rem" },
  letterSpacing: "-0.005em",
  // Dull black — clearly above the lighter body / labels without being heavy.
  color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : "#374151"),
  mb: 0.35,
  lineHeight: 1.35,
};

/** Lays out a single icon beside a section heading (e.g. Details, Files). Color set per-icon. */
export const taskFormSectionTitleWithIconSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.85,
  mb: 0.5,
  "& > svg": {
    fontSize: 19,
    flexShrink: 0,
  },
  // Neutralize the heading's own bottom margin so it sits centered with the icon.
  "& > .MuiTypography-root": {
    mb: 0,
  },
};

/** Sizing for the colored icon beside a feature row label (Type / Status / Priority). Color set per-icon. */
export const taskFormFieldLabelIconSx = {
  fontSize: 18,
  flexShrink: 0,
};

/** Wraps the leading icon + label text into a feature row's first (label) column. */
export const taskFormFieldLabelWithIconSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
};

export const taskFormBodyTextSx = {
  fontSize: { xs: "0.875rem", sm: "0.9375rem" },
  lineHeight: 1.5,
  color: (theme) => (isDark(theme) ? TASK_FORM_DARK_TEXT : TASK_FORM_LIGHT_TEXT),
};

/** Label + value on one row — label column sizes to content. */
export const taskFormFieldRowSx = {
  display: "grid",
  gridTemplateColumns: "minmax(80px, max-content) minmax(0, 1fr)",
  columnGap: { xs: 2.5, sm: 3 },
  rowGap: 0.25,
  alignItems: "center",
};

export const taskFormFieldRowStartSx = {
  ...taskFormFieldRowSx,
  alignItems: "center",
  gridTemplateColumns: "max-content minmax(0, 1fr)",
  columnGap: { xs: 0.75, sm: 1 },
};

export const taskFormAutocompleteSx = {
  flex: 1,
  minWidth: { xs: "100%", sm: 220 },
  "& .MuiInputBase-root": {
    minHeight: { xs: 38, sm: 40 },
    maxHeight: { xs: 38, sm: 40 },
    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
    borderRadius: "8px",
  },
  "& .MuiInputBase-input": {
    py: "7px",
    fontSize: { xs: "0.875rem", sm: "0.9375rem" },
  },
};

export const taskFormAddNewButtonSx = {
  textTransform: "none",
  whiteSpace: "nowrap",
  alignSelf: { xs: "stretch", sm: "flex-start" },
  minHeight: { xs: 38, sm: 40 },
  px: { xs: 1.5, sm: 1.75 },
  fontSize: { xs: "0.875rem", sm: "0.9rem" },
  fontWeight: 600,
  borderRadius: "8px",
  borderWidth: "1.5px",
  mt: { xs: 0, sm: 0 },
  "&:hover": {
    borderWidth: "1.5px",
  },
};

/** Small inline action button used beside a field value (e.g. Edit assignees). */
export const taskFormInlineButtonSx = {
  textTransform: "none",
  whiteSpace: "nowrap",
  minHeight: 28,
  height: 28,
  py: 0,
  px: 1,
  fontSize: "0.8rem",
  fontWeight: 500,
  borderRadius: "7px",
  borderWidth: "1px",
  alignSelf: "center",
  "& .MuiButton-startIcon": { mr: 0.4, "& > svg": { fontSize: "0.9rem" } },
  "&:hover": { borderWidth: "1px" },
};

export const taskFormRecurrenceInnerSx = {
  display: "flex",
  alignItems: "flex-start",
  position: "relative",
  px: { xs: 1.5, sm: 1.75 },
  py: { xs: 1, sm: 1.15 },
  borderRadius: "8px",
  maxWidth: "100%",
  minWidth: 0,
  flex: 1,
  transition: "background-color 0.2s ease",
  "& .recurrence-remove-btn": {
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity 0.2s ease",
  },
  "&:hover": {
    bgcolor: (theme) => (isDark(theme) ? "rgba(255, 255, 255, 0.06)" : "#eef2f4"),
  },
  "&:hover .recurrence-remove-btn": {
    opacity: 1,
    pointerEvents: "auto",
  },
};

export const taskFormRecurrenceSummarySx = {
  color: "text.primary",
  fontSize: { xs: "0.975rem", sm: "1.05rem" },
  lineHeight: 1.55,
  fontWeight: 400,
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  pr: 2.5,
};

export const taskFormScrollContentSx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  p: { xs: 1.5, sm: 2.25 },
  // Slim, light scrollbar (Firefox + WebKit).
  scrollbarWidth: "thin",
  scrollbarColor: (theme) =>
    isDark(theme) ? "rgba(255, 255, 255, 0.22) transparent" : "rgba(15, 23, 42, 0.18) transparent",
  "&::-webkit-scrollbar": { width: 6, height: 6 },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 8,
    backgroundColor: (theme) =>
      isDark(theme) ? "rgba(255, 255, 255, 0.22)" : "rgba(15, 23, 42, 0.18)",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: (theme) =>
      isDark(theme) ? "rgba(255, 255, 255, 0.34)" : "rgba(15, 23, 42, 0.28)",
  },
};

export const taskFormStickyFooterSx = {
  flexShrink: 0,
  position: "sticky",
  bottom: 0,
  zIndex: 4,
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 1,
  px: { xs: 1.75, sm: 2 },
  py: { xs: 1, sm: 1.25 },
  borderTop: (theme) =>
    `1px solid ${isDark(theme) ? TASK_FORM_DARK_BORDER : TASK_FORM_LIGHT_BORDER}`,
  bgcolor: (theme) => (isDark(theme) ? TASK_FORM_DARK_BG : TASK_FORM_LIGHT_SECTION_BG),
};

export const taskFormPrimaryButtonSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: { xs: "0.875rem", sm: "0.9rem" },
  px: { xs: 2, sm: 2.5 },
  py: { xs: 0.6, sm: 0.7 },
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

export const taskFormSecondaryButtonSx = {
  textTransform: "none",
  borderRadius: "8px",
  fontSize: { xs: "0.875rem", sm: "0.9rem" },
  px: { xs: 1.25, sm: 1.5 },
  py: { xs: 0.6, sm: 0.7 },
  color: (theme) => (isDark(theme) ? undefined : TASK_FORM_LIGHT_LABEL),
};

export const taskFormNameInputSx = {
  flex: 1,
  mr: 1,
  "& .MuiInputBase-root": {
    bgcolor: "transparent !important",
  },
  "& .MuiInput-underline:before": { borderBottom: "none !important" },
  "& .MuiInput-underline:after": { borderBottom: "none !important" },
  "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottom: "none !important" },
  "& .MuiInputBase-input": {
    fontSize: { xs: "1.05rem", sm: "1.15rem" },
    fontWeight: 600,
    py: 0.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

/**
 * Section-toggle pill (Type / Status / Files / Checklists …).
 * Background stays white (surface) at all times. Text + icon are the default
 * dark color when idle and brand green when selected — never a solid fill.
 */
export const taskFormChipSx = (active = false) => ({
  borderRadius: "8px",
  fontSize: "0.8125rem",
  fontWeight: active ? 600 : 500,
  height: 30,
  bgcolor: (theme) => (isDark(theme) ? TASK_FORM_DARK_SURFACE : TASK_FORM_LIGHT_SECTION_BG),
  borderColor: (theme) =>
    active
      ? alpha(theme.palette.primary.main, isDark(theme) ? 0.55 : 0.45)
      : isDark(theme)
      ? TASK_FORM_DARK_BORDER
      : TASK_FORM_LIGHT_BORDER,
  color: (theme) =>
    active
      ? theme.palette.primary.main
      : isDark(theme)
      ? TASK_FORM_DARK_TEXT
      : TASK_FORM_LIGHT_TEXT,
  "& .MuiChip-label": { px: 1 },
  "& .MuiChip-icon": {
    fontSize: 16,
    ml: "6px",
    color: "inherit",
  },
  "&:hover": {
    bgcolor: (theme) =>
      isDark(theme) ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
    borderColor: (theme) =>
      active
        ? theme.palette.primary.main
        : isDark(theme)
        ? TASK_FORM_DARK_MUTED
        : "#a8adb4",
  },
});

/** Task edit + chat split only (45% form / 55% chat). */
export const taskFormColumnSplitFormSx = {
  flex: { xs: "1 1 auto", sm: "0 1 45%" },
  minWidth: { sm: 0 },
  maxWidth: { sm: "45%" },
  width: { sm: "45%" },
};

export const taskFormColumnSplitChatSx = {
  flex: { xs: "1 1 auto", sm: "1 1 55%" },
  minWidth: { xs: 0, sm: 0 },
  maxWidth: { sm: "55%" },
  width: { sm: "55%" },
};

/** Recurring task dialog — section labels and option text */
export const taskFormRecurrenceDialogLabelSx = {
  color: "text.secondary",
  fontSize: { xs: "0.975rem", sm: "1.0625rem" },
  fontWeight: 500,
  lineHeight: 1.45,
};

export const taskFormRecurrenceDialogTextSx = {
  fontSize: { xs: "0.975rem", sm: "1.02rem" },
  lineHeight: 1.45,
};

export const taskFormRecurrenceOptionLabelSx = {
  m: 0,
  alignItems: "center",
  "& .MuiFormControlLabel-label": {
    fontSize: { xs: "0.975rem", sm: "1.02rem" },
    lineHeight: 1.45,
  },
};

export const taskFormRecurrencePickerSlotProps = {
  textField: {
    size: "medium",
    sx: {
      "& .MuiInputBase-input": {
        fontSize: { xs: "0.975rem", sm: "1rem" },
      },
    },
  },
};

export const taskFormRecurrenceSoftBoxPadding = { p: 1.35, borderRadius: 1 };

export const taskFormRecurrenceSubheadingSx = {
  fontWeight: 600,
  fontSize: { xs: "1rem", sm: "1.0625rem" },
  lineHeight: 1.35,
  mb: 0.85,
};

export const taskFormRecurrenceCadenceToggleSx = {
  "& .MuiToggleButton-root": {
    fontSize: { xs: "0.9375rem", sm: "1rem" },
    fontWeight: 500,
    py: 1.15,
    px: 1.25,
    textTransform: "none",
    lineHeight: 1.35,
  },
};

/** Descendant sizing for cadence panels (daily / weekly / monthly / yearly). */
export const taskFormRecurrencePanelSx = {
  "& .MuiFormControlLabel-label": {
    fontSize: { xs: "0.975rem", sm: "1.02rem" },
    lineHeight: 1.45,
  },
  "& .MuiInputBase-input": {
    fontSize: { xs: "0.975rem", sm: "1rem" },
  },
  "& .MuiSelect-select": {
    fontSize: { xs: "0.975rem", sm: "1rem" },
    py: 1.05,
  },
  "& .MuiButton-root": {
    fontSize: { xs: "0.9375rem", sm: "0.975rem" },
    textTransform: "none",
  },
};

export const taskFormRecurrenceInlineNumberSx = {
  width: 80,
  "& .MuiInputBase-root": {
    minHeight: 42,
  },
};

export const taskFormRecurrenceWeekdayLabelSx = {
  fontSize: { xs: "0.925rem", sm: "0.975rem" },
  lineHeight: 1.35,
};
